import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { ShieldCheck, HeartPulse, PhoneCall } from "lucide-react";

function useCountdown(dueIso) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.round((new Date(dueIso) - Date.now()) / 60000)));
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [dueIso]);
  return left;
}

export default function SafetyCheckIn({ request, onChanged, compact = false }) {
  const [busy, setBusy] = useState(false);
  const left = useCountdown(request.safety_due_at);

  const safe = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${request.id}/safe`);
      toast.success("Thank you — glad you're safe!");
      onChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const s = request.safety_status;
  if (!s) return null;
  if (s === "safe") {
    return (
      <div data-testid="safety-safe" className="flex items-center gap-3 bg-[#E8F4F1] text-[#1F5F4F] rounded-2xl p-4 font-semibold">
        <ShieldCheck className="w-7 h-7" /> You checked in safe{request.safe_at ? ` at ${new Date(request.safe_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}.
      </div>
    );
  }
  if (s === "alerted") {
    return (
      <div data-testid="safety-alerted" className="flex items-start gap-3 bg-[#FFF7E6] text-[#A9741A] rounded-2xl p-4">
        <PhoneCall className="w-7 h-7 shrink-0" />
        <div><p className="font-semibold">We let your family contact know you hadn't checked in yet.</p><p className="mt-1">If you're okay, tap below so everyone can relax.</p>
          <button data-testid="im-safe-btn" onClick={safe} disabled={busy} className="mt-3 bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-bold px-6 min-h-[52px] rounded-xl text-lg">I'm safe</button>
        </div>
      </div>
    );
  }
  if (s === "no_contact" && compact) return null;
  return (
    <div data-testid="safety-pending" className={`bg-white border-2 border-[#D96B43] rounded-2xl ${compact ? "p-4" : "p-6"} flex flex-col sm:flex-row sm:items-center gap-4`}>
      <span className="w-14 h-14 rounded-2xl bg-[#FBEBE4] flex items-center justify-center shrink-0"><HeartPulse className="w-8 h-8 text-[#D96B43]" /></span>
      <div className="flex-1">
        <p className="text-xl font-semibold text-slate-900">{compact ? request.title : "Your visit is complete — are you home safe?"}</p>
        <p className="text-slate-600 mt-1">
          {s === "no_contact"
            ? "Tap to let us know you're okay."
            : left > 0 ? `If you don't check in within ${left} minute${left === 1 ? "" : "s"}, we'll gently let your family contact know.` : "Checking in now will reassure your family contact."}
        </p>
      </div>
      <button data-testid="im-safe-btn" onClick={safe} disabled={busy}
        className="bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-bold px-8 min-h-[60px] rounded-xl text-xl flex items-center justify-center gap-2 transition-colors">
        <ShieldCheck className="w-6 h-6" /> I'm safe
      </button>
    </div>
  );
}
